import React, { useState, useEffect } from 'react';
import { BugReportService } from '../../../firebase/unifiedDataService';
import { Card } from '../../../components/ui/BaseComponents';
import { AlertBanner } from '../../../components/ui';
import { Logger } from '../../../utils/logger';

const log = Logger.create('BugReportsTab');

/**
 * BugReportsTab - Display and manage bug reports in Admin Dashboard
 * 
 * Uses unifiedDataService.BugReportService for consistent data access
 */
export default function BugReportsTab() {
  const [bugReports, setBugReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedReport, setSelectedReport] = useState(null);

  // Load bug reports via BugReportService
  useEffect(() => {
    loadBugReports();
  }, []);

  const loadBugReports = async () => {
    try {
      setLoading(true);
      const reports = await BugReportService.getAll();
      setBugReports(reports);
    } catch (err) {
      log.error('Error loading bug reports:', err);
      setError('Fehler beim Laden der Bug-Reports');
    } finally {
      setLoading(false);
    }
  };

  // Update bug report status
  const updateStatus = async (reportId, newStatus) => {
    try {
      await BugReportService.update(reportId, { status: newStatus });
      
      setBugReports(prev => prev.map(report => 
        report.id === reportId ? { ...report, status: newStatus } : report
      ));
      
      if (selectedReport?.id === reportId) {
        setSelectedReport(prev => ({ ...prev, status: newStatus }));
      }
    } catch (err) {
      log.error('Error updating status:', err);
      setError('Fehler beim Aktualisieren des Status');
    }
  };

  // Update bug report priority
  const updatePriority = async (reportId, newPriority) => {
    try {
      await BugReportService.update(reportId, { priority: newPriority });
      
      setBugReports(prev => prev.map(report => 
        report.id === reportId ? { ...report, priority: newPriority } : report
      ));
      
      if (selectedReport?.id === reportId) {
        setSelectedReport(prev => ({ ...prev, priority: newPriority }));
      }
    } catch (err) {
      log.error('Error updating priority:', err);
      setError('Fehler beim Aktualisieren der Priorität');
    }
  };

  // Delete bug report
  const deleteBugReport = async (reportId) => {
    if (!confirm('Bug-Report wirklich löschen?')) return;
    
    try {
      await BugReportService.delete(reportId);
      setBugReports(prev => prev.filter(report => report.id !== reportId));
      
      if (selectedReport?.id === reportId) {
        setSelectedReport(null);
      }
    } catch (err) {
      log.error('Error deleting bug report:', err);
      setError('Fehler beim Löschen des Bug-Reports');
    }
  };

  // Filter bug reports
  const filteredReports = bugReports.filter(report => {
    const matchesStatus = filterStatus === 'all' || report.status === filterStatus;
    const matchesCategory = filterCategory === 'all' || report.category === filterCategory;
    const matchesSearch = !searchTerm || 
      report.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      report.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      report.userEmail?.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesStatus && matchesCategory && matchesSearch;
  });

  // Stats
  const stats = {
    total: bugReports.length,
    new: bugReports.filter(r => r.status === 'new').length,
    inProgress: bugReports.filter(r => r.status === 'in-progress').length,
    resolved: bugReports.filter(r => r.status === 'resolved').length,
    closed: bugReports.filter(r => r.status === 'closed').length
  };

  // Category colors
  const getCategoryColor = (category) => {
    const colors = {
      ui: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
      functionality: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
      performance: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
      data: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
      other: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
    };
    return colors[category] || colors.other;
  };

  // Status colors
  const getStatusColor = (status) => {
    const colors = {
      new: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
      'in-progress': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
      resolved: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
      closed: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
    };
    return colors[status] || colors.new;
  };

  // Priority colors
  const getPriorityColor = (priority) => {
    const colors = {
      low: 'text-gray-500',
      medium: 'text-yellow-600',
      high: 'text-red-600'
    };
    return colors[priority] || colors.medium;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return new Intl.DateTimeFormat('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <AlertBanner
          variant="danger"
          message={error}
          onDismiss={() => setError('')}
        />
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="p-4">
          <div className="text-2xl font-bold text-foreground">{stats.total}</div>
          <div className="text-sm text-muted-foreground">Gesamt</div>
        </Card>
        <Card className="p-4 bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-700">
          <div className="text-2xl font-bold text-red-900 dark:text-red-200">{stats.new}</div>
          <div className="text-sm text-red-600 dark:text-red-400">Neu</div>
        </Card>
        <Card className="p-4 bg-yellow-50 dark:bg-yellow-900/30 border-yellow-200 dark:border-yellow-700">
          <div className="text-2xl font-bold text-yellow-900 dark:text-yellow-200">{stats.inProgress}</div>
          <div className="text-sm text-yellow-600 dark:text-yellow-400">In Bearbeitung</div>
        </Card>
        <Card className="p-4 bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-700">
          <div className="text-2xl font-bold text-green-900 dark:text-green-200">{stats.resolved}</div>
          <div className="text-sm text-green-600 dark:text-green-400">Gelöst</div>
        </Card>
        <Card className="p-4 bg-gray-50 dark:bg-gray-800">
          <div className="text-2xl font-bold text-foreground">{stats.closed}</div>
          <div className="text-sm text-muted-foreground">Geschlossen</div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Status
            </label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
            >
              <option value="all">Alle</option>
              <option value="new">Neu</option>
              <option value="in-progress">In Bearbeitung</option>
              <option value="resolved">Gelöst</option>
              <option value="closed">Geschlossen</option>
            </select>
          </div>

          {/* Category Filter */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Kategorie
            </label>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
            >
              <option value="all">Alle</option>
              <option value="ui">UI/Design</option>
              <option value="functionality">Funktionalität</option>
              <option value="performance">Performance</option>
              <option value="data">Daten</option>
              <option value="other">Sonstiges</option>
            </select>
          </div>

          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Suche
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Titel, Beschreibung, Email..."
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
            />
          </div>
        </div>
      </Card>

      {/* Bug Reports Table */}
      <Card className="overflow-hidden">
        {filteredReports.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <svg className="w-16 h-16 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-lg font-medium">Keine Bug-Reports gefunden</p>
            <p className="text-sm mt-1">Versuche andere Filter oder Suchbegriffe</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50 border-b border-border">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Titel
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Kategorie
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Priorität
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Benutzer
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Datum
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Aktionen
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredReports.map(report => (
                  <tr 
                    key={report.id}
                    className="hover:bg-muted/50 cursor-pointer theme-transition"
                    onClick={() => setSelectedReport(report)}
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-foreground">{report.title}</div>
                      <div className="text-sm text-muted-foreground truncate max-w-xs">
                        {report.description}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getCategoryColor(report.category)}`}>
                        {report.category === 'ui' && 'UI/Design'}
                        {report.category === 'functionality' && 'Funktionalität'}
                        {report.category === 'performance' && 'Performance'}
                        {report.category === 'data' && 'Daten'}
                        {report.category === 'other' && 'Sonstiges'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={report.status}
                        onChange={(e) => {
                          e.stopPropagation();
                          updateStatus(report.id, e.target.value);
                        }}
                        className={`px-2 py-1 text-xs font-medium rounded-full border-0 cursor-pointer ${getStatusColor(report.status)}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <option value="new">Neu</option>
                        <option value="in-progress">In Bearbeitung</option>
                        <option value="resolved">Gelöst</option>
                        <option value="closed">Geschlossen</option>
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={report.priority || 'medium'}
                        onChange={(e) => {
                          e.stopPropagation();
                          updatePriority(report.id, e.target.value);
                        }}
                        className={`px-2 py-1 text-xs font-medium border border-border rounded cursor-pointer bg-background ${getPriorityColor(report.priority)}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <option value="low">Niedrig</option>
                        <option value="medium">Mittel</option>
                        <option value="high">Hoch</option>
                      </select>
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground">
                      {report.userEmail || 'Anonym'}
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {formatDate(report.createdAt)}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteBugReport(report.id);
                        }}
                        className="text-red-600 hover:text-red-800 dark:hover:text-red-400"
                        title="Löschen"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Detail Modal */}
      {selectedReport && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedReport(null)}
        >
          <div 
            className="bg-background rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-border flex items-start justify-between">
              <div className="flex-1">
                <h3 className="text-xl font-bold text-foreground mb-2">{selectedReport.title}</h3>
                <div className="flex items-center gap-2">
                  <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getCategoryColor(selectedReport.category)}`}>
                    {selectedReport.category === 'ui' && 'UI/Design'}
                    {selectedReport.category === 'functionality' && 'Funktionalität'}
                    {selectedReport.category === 'performance' && 'Performance'}
                    {selectedReport.category === 'data' && 'Daten'}
                    {selectedReport.category === 'other' && 'Sonstiges'}
                  </span>
                  <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(selectedReport.status)}`}>
                    {selectedReport.status === 'new' && 'Neu'}
                    {selectedReport.status === 'in-progress' && 'In Bearbeitung'}
                    {selectedReport.status === 'resolved' && 'Gelöst'}
                    {selectedReport.status === 'closed' && 'Geschlossen'}
                  </span>
                </div>
              </div>
              <button
                onClick={() => setSelectedReport(null)}
                className="text-muted-foreground hover:text-foreground"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <h4 className="text-sm font-medium text-muted-foreground mb-1">Beschreibung</h4>
                <p className="text-foreground whitespace-pre-wrap">{selectedReport.description}</p>
              </div>
              
              {selectedReport.stepsToReproduce && (
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-1">Schritte zur Reproduktion</h4>
                  <p className="text-foreground whitespace-pre-wrap">{selectedReport.stepsToReproduce}</p>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-border">
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-1">Benutzer</h4>
                  <p className="text-foreground">{selectedReport.userEmail || 'Anonym'}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-1">Datum</h4>
                  <p className="text-foreground">{formatDate(selectedReport.createdAt)}</p>
                </div>
              </div>
              
              {selectedReport.systemInfo && (
                <div className="pt-4 border-t border-border">
                  <h4 className="text-sm font-medium text-muted-foreground mb-2">System-Informationen</h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-muted-foreground">Browser:</span>
                      <span className="text-foreground ml-2">{selectedReport.systemInfo.browser}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">OS:</span>
                      <span className="text-foreground ml-2">{selectedReport.systemInfo.os}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Viewport:</span>
                      <span className="text-foreground ml-2">
                        {selectedReport.systemInfo.viewport?.width} × {selectedReport.systemInfo.viewport?.height}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Theme:</span>
                      <span className="text-foreground ml-2">{selectedReport.systemInfo.theme}</span>
                    </div>
                    <div className="col-span-2">
                      <span className="text-muted-foreground">URL:</span>
                      <span className="text-foreground ml-2 break-all">{selectedReport.systemInfo.url}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
